import { breakpoints } from '../breakpoints';
import { Color } from '../color';
import { Size } from '../size';

/**
 * The Modal shell's rules, with no DOM inside (piece 5, decision 0242). The
 * shell itself is a native `dialog` opened with `showModal()` (decision 0070),
 * so the browser carries the top layer, the backdrop, the focus trap,
 * Escape-to-close and the inertness of the page behind. What is left over is
 * here: which layer is interactive, what a footer may hold, and where focus
 * goes when a layer closes.
 */

/**
 * How the shell sizes itself at a given viewport width (piece 5).
 *
 * A modal is written for the phone first and widened, never a desktop card
 * shrunk onto a phone — but narrow-first governs its size, not its position.
 * 🔨 **The modal is a centred card at every width** (product owner,
 * 2026-09-14). Below a tablet it is `full`: nearly the screen's whole width,
 * as tall as the screen really shows, with the Body taking whatever height the
 * Title Bar and Footer leave it. From a tablet up it is `card`: capped at its
 * own maximum width, with the Body stopping short of filling a screen that has
 * room to spare.
 *
 * The one width the two sizes divide at, and it is the canonical scale's own
 * tablet step rather than a number invented here. `modal.module.css` reads the
 * same step as `@media (--bp-md)`, and a test in the rendering layer holds the
 * stylesheet to this constant.
 */
export const MODAL_CARD_FROM = breakpoints.md;

/**
 * The Body is the only region that scrolls, and its scroll stops at its own
 * ends rather than chaining to the page behind it. Nothing switches this on;
 * the constant is here so the rule is a fact a test can read rather than a
 * line in a stylesheet nobody notices deleting.
 */
export const MODAL_BODY_OWNS_THE_SCROLL = true;

/** The three ways out, and they are one outcome (piece 5). */
export type ModalExit = 'close' | 'cancel' | 'escape';
export const MODAL_EXITS: readonly ModalExit[] = ['close', 'cancel', 'escape'];

/**
 * A backdrop click never cancels a modal: a stray click outside a form is not
 * a decision to discard what was typed into it. Nothing switches this on, and
 * the constant is here so the rule is a fact a test can read rather than the
 * absence of a handler nobody notices adding.
 */
export const BACKDROP_CLICK_CANCELS = false;

/** Every Footer button is the Large size, whose 38px height clears the 37px minimum. */
export const MODAL_FOOTER_BUTTON_SIZE = Size.Large;

/** The Footer's Primary is navy in every modal; red is this application's destructive cue. */
export const MODAL_PRIMARY_COLOR = Color.Navy;

export interface ModalLayer {
  id: string;
  /** The control that opened this layer, so closing can hand focus back to it. */
  openerId: string | null;
}

export type ModalStackEvent = { type: 'open'; layer: ModalLayer } | { type: 'close'; id: string };

/**
 * A modal opens over a modal (decision 0138). The stack is the order they
 * opened in; the layer beneath stays open and inert rather than closing.
 */
export function modalStackReducer(stack: readonly ModalLayer[], event: ModalStackEvent): ModalLayer[] {
  switch (event.type) {
    case 'open':
      return stack.some((l) => l.id === event.layer.id) ? [...stack] : [...stack, event.layer];
    case 'close':
      return stack.filter((l) => l.id !== event.id);
  }
}

export function topLayer(stack: readonly ModalLayer[]): ModalLayer | undefined {
  return stack.at(-1);
}

/** Only the topmost layer is interactive, and only it takes Escape. */
export function isTopmost(stack: readonly ModalLayer[], id: string): boolean {
  return topLayer(stack)?.id === id;
}

/** An open layer that is not the top one: still there, and inert. */
export function isInert(stack: readonly ModalLayer[], id: string): boolean {
  return stack.some((l) => l.id === id) && !isTopmost(stack, id);
}

/**
 * Closing a layer returns focus to exactly the control that opened it, never
 * to the top of the document — and on a stack that is a control inside the
 * layer beneath, not the page behind the whole stack.
 */
export function focusReturnTarget(stack: readonly ModalLayer[], id: string): string | null {
  return stack.find((l) => l.id === id)?.openerId ?? null;
}

/** Escape closes the top layer only; a lower layer ignores it. */
export function escapeCloses(stack: readonly ModalLayer[]): string | null {
  return topLayer(stack)?.id ?? null;
}

export type FooterActionKind = 'primary' | 'cancel' | 'destructiveSecondary' | 'close';

export class ModalFooterError extends Error {}

/**
 * A Footer holds a Primary, a Cancel and at most one destructive-outlined
 * secondary action. A second destructive action, a second Primary or a fourth
 * control of any kind is refused here rather than drawn.
 *
 * 🔨 A window that writes nothing carries one Close and nothing else (decision
 * 2140): the `×`, Escape and that Close are one outcome, so a Primary and a
 * Cancel beside it would be two buttons for one result.
 */
export function assertFooterShape(kinds: readonly FooterActionKind[]): void {
  if (kinds.includes('close')) {
    if (kinds.length !== 1) throw new ModalFooterError(`a modal Footer that carries Close carries nothing else; this one holds ${kinds.length} controls (piece 5)`);
    return;
  }
  const count = (kind: FooterActionKind) => kinds.filter((k) => k === kind).length;
  if (count('primary') !== 1) throw new ModalFooterError(`a modal Footer holds exactly one Primary; this one holds ${count('primary')} (piece 5)`);
  if (count('cancel') !== 1) throw new ModalFooterError(`a modal Footer holds exactly one Cancel; this one holds ${count('cancel')} (piece 5)`);
  if (count('destructiveSecondary') > 1) {
    throw new ModalFooterError(`a modal Footer holds at most one destructive-outlined secondary action; this one holds ${count('destructiveSecondary')} (piece 5)`);
  }
  if (kinds.length > 3) throw new ModalFooterError(`a modal Footer holds no control beyond the Primary, the Cancel and one destructive secondary (piece 5)`);
}

/**
 * A modal opens and closes inline on the current page and never causes a route
 * change, so the address and the history length are the same before and after.
 */
export interface RouteFootprint {
  href: string;
  historyLength: number;
}

export function routeUnchanged(before: RouteFootprint, after: RouteFootprint): boolean {
  return before.href === after.href && before.historyLength === after.historyLength;
}
